<template>
  <!-- * * * * * * * Mobile
  -->
  <div
    v-if="uiStore.isMobile"
    class="w-full
    flex flex-col desk:flex-row gap-8 desk:gap-24 desk:justify-between desk:items-center
    border-b border-light py-12 mobOnly:px-12"
  >
    <!-- image, name and stock -->
    <nuxt-link :to="link" class="flex justify-start gap-8 desk:gap-12 desk:basis-[40%]">
      <!-- image -->
      <div class="basis-[54px] desk:basis-64 shrink-0 relative">
        <img
          v-if="!quickBuyActiveVariant.image"
          src="/icons/no-image.svg"
          class="object-cover w-[54px] desk:w-64 aspect-square "
          alt=""
        >
        <nuxt-img
          v-else
          :src="quickBuyActiveVariant.image"
          provider="norce"
          preset="plpimage"
          sizes="mob375:56px"
          class="object-cover w-[54px] desk:w-64 aspect-square group-hover:scale-110 transition ease-out duration-300"
          :alt="productHydrated.name"
          :loading="placement > 1 ? 'lazy':'eager'"
        />
        <div class="imageOverlay absolute top-0 left-0 w-full h-full pointer-events-none"/>
      </div>
      <!-- name etc -->
      <div class="type-sm">
        <div class="type-headline-sm mb-4">{{ quickBuyActiveVariant.name }}</div>

        <div
          v-if="globalContent.getCountryCode === 'de' && isFloor"
          class="mb-4"
        >
          {{ productHydrated.parametricByCode('ProductConstruction')?.listValue?.name }}
        </div>
        <div v-else-if="productHydrated.lastChildCategory?.name" class="mb-4">
          {{ productHydrated.lastChildCategory.name }}
        </div>
        <StockStatus
          v-if="userPrice.status !== 'notLoggedIn' && !hideResilientStockStatus && !globalContent.disablePricingAndPurchase"
          :variant="quickBuyActiveVariant"
          :product-item="productHydrated"
          :use-short-labels="true"
          :is-product-card="true"
          :use-area="true"
          use-primary-stock-status
        />
        <div v-else-if="globalContent.getCountryCode === 'de'" class="type-xs">{{ $t('resilient.contactSales') }}</div>
        <span class="hidden">{{ productHydrated.priceFetchPartNo }}</span>
      </div>
    </nuxt-link>

    <!-- variants -->
    <VariantSelector
      v-if="userStore.isLoggedIn && !product.isStructureArticle && productHydrated.hasAnyBuyableVariant"
      class="relative desk:basis-[18%]"
      :product-item="productHydrated"
      :active-variant="quickBuyActiveVariant"
      design="list"
      @update-active-variant="(variant) => quickBuyActiveVariant = variant"
    />

    <div v-if="userPrice.status === 'notLoggedIn' && !globalContent.getShowPriceNonLoggedInB2B">
      <div
        class="btn--text"
        @click.prevent="showLogin"
      >
        {{ $t('price.notLoggedIn') }}
      </div>
    </div>
    <!-- amount selector, price and buy -->
    <div
      v-else
      class="desk:basis-[42%] flex justify-between desk:justify-end items-center gap-8"
    >
      <!-- amount selector -->
      <PackageToAreaConverter
        v-if="shouldUseArea && productHydrated.hasAnyBuyableVariant && (!globalContent.getIsB2c || productHydrated.isD2C) && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser"
        v-model="totalQuantity"
        :input="quickBuyActiveVariant"
        design="plpcompact"
      />

      <TouchNumber
        v-else-if="canPurchaseProduct && (productHydrated.isD2C ? quickBuyActiveVariant.isInStock : quickBuyActiveVariant.canAddToCart) && (!globalContent.getIsB2c || (productHydrated.isD2C && globalContent.getNorceCheckoutEnabled)) && !userStore.isSamplesOnlyUser"
        v-model="totalQuantity"
        :show-buttons-on-desktop="true"
        class="basis-[130px]"
      />
      <!-- price and buy -->
      <div v-if="!hideResilientProducts && !globalContent.disablePricingAndPurchase && !globalContent.disablePricingB2C" class="flex gap-8 items-center">
        <ProductPrice
          :crossell="false"
          :product-item="productHydrated"
          :active-variant="quickBuyActiveVariant"
          design="plpCompact"
          class="text-right w-72"
        />
        <button
          v-if="canPurchaseProduct && (!globalContent.getIsB2c || (productHydrated.isD2C && globalContent.getNorceCheckoutEnabled)) && !userStore.isSamplesOnlyUser"
          class="btn btn--wIcon !w-32 !h-32 desk:w-40 desk:h-40"
          :aria-label="$t('quickbuy.button')"
          :disabled="productHydrated.isD2C ? !quickBuyActiveVariant.isInStock : !quickBuyActiveVariant.canAddToCart"
          :class="{
            loading: buyLoading,
          }"
          type="button"
          @click="buyProduct"
        >
          <img
            src="/icons/cart-inv.svg"
            class="inline-block w-20"
            alt="cart"
          >
        </button>
      </div>
    </div>
  </div>
  <!-- * * * * Desktop -->
  <tr
    v-else
    class="w-full border-b border-light "
  >
    <!-- image, name and stock -->
    <td class="w-86  py-12 pr-24">
      <!-- Pointer-only duplicate of the name link in the next cell; hidden from AT -->
      <nuxt-link
        :to="link"
        aria-hidden="true"
        tabindex="-1"
        class="inline-block relative"
      >
        <img
          v-if="!quickBuyActiveVariant.image"
          src="/icons/no-image.svg"
          class="object-cover w-64 aspect-square "
          alt=""
        >
        <nuxt-img
          v-else
          :src="quickBuyActiveVariant.image"
          provider="norce"
          preset="plpimage"
          sizes="mob375:56px"
          class="object-cover w-64 aspect-square group-hover:scale-110 transition ease-out duration-300"
          :alt="productHydrated.name"
          :loading="placement > 1 ? 'lazy':'eager'"
        />
        <div class="imageOverlay absolute top-0 left-0 w-full h-full pointer-events-none"/>
      </nuxt-link>
    </td>

    <td class="py-12 pr-24">
      <nuxt-link :to="link">
        <!-- name etc -->
        <div class="type-sm">
          <div class="type-headline-sm mb-4">{{ quickBuyActiveVariant.name }}</div>

          <div
            v-if="globalContent.getCountryCode === 'de' && isFloor"
            class="mb-4"
          >
            {{ productHydrated.parametricByCode('ProductConstruction')?.listValue?.name }}
          </div>
          <div v-else-if="productHydrated.lastChildCategory?.name" class="mb-4">
            {{ productHydrated.lastChildCategory.name }}
          </div>
          <StockStatus
            v-if="userPrice.status !== 'notLoggedIn' && !hideResilientStockStatus && !globalContent.disablePricingAndPurchase && !globalContent.getIsB2c"
            :variant="quickBuyActiveVariant"
            :product-item="productHydrated"
            :use-short-labels="true"
            :is-product-card="true"
            :use-area="true"
            use-primary-stock-status
          />
          <div v-else-if="globalContent.getCountryCode === 'de'" class="type-xs">{{ $t('resilient.contactSales') }}</div>
          <span class="hidden">{{ productHydrated.priceFetchPartNo }}</span>
        </div>
      </nuxt-link>
    </td>

    <!-- variants -->
    <td
      v-if="userStore.isLoggedIn && !product.isStructureArticle && productHydrated.hasAnyBuyableVariant"
      class="pr-24"
    >
      <VariantSelector
        class="relative desk:basis-[18%]"
        :product-item="productHydrated"
        :active-variant="quickBuyActiveVariant"
        design="list"
        @update-active-variant="(variant) => quickBuyActiveVariant = variant"
      />
    </td>
    <td v-else/>

    <td
      v-if="userPrice.status === 'notLoggedIn' && !globalContent.getShowPriceNonLoggedInB2B"
      class="text-right pr-24"
    >
      <div
        class="btn--text inline"
        @click.prevent="showLogin"
      >
        {{ $t('price.notLoggedIn') }}
      </div>
    </td>
    <!-- amount selector, price and buy -->
    <td
      v-if="userPrice.status !== 'notLoggedIn' || globalContent.getShowPriceNonLoggedInB2B"
      class="py-12 pr-24"
    >
      <div class="flex justify-end"><!-- amount selector -->
        <PackageToAreaConverter
          v-if="shouldUseArea && productHydrated.hasAnyBuyableVariant && (!globalContent.getIsB2c || productHydrated.isD2C) && !userStore.isSamplesOnlyUser && !userStore.isMediabankUser"
          v-model="totalQuantity"
          :input="quickBuyActiveVariant"
          design="plpcompact"
        />
        <TouchNumber
          v-else-if="canPurchaseProduct && (productHydrated.isD2C ? quickBuyActiveVariant.isInStock : quickBuyActiveVariant.canAddToCart) && (!globalContent.getIsB2c || (productHydrated.isD2C && globalContent.getNorceCheckoutEnabled)) && !userStore.isSamplesOnlyUser"
          v-model="totalQuantity"
          :show-buttons-on-desktop="true"
          class="basis-[130px]"
        />
      </div>
    </td>

    <td
      v-if="!hideResilientProducts && !globalContent.disablePricingAndPurchase && !globalContent.disablePricingB2C && userPrice.status !== 'notLoggedIn'"
      class="py-12"
    >
      <!-- price and buy -->

      <div class="flex gap-8 items-center justify-end">
        <ProductPrice
          :crossell="false"
          :product-item="productHydrated"
          :active-variant="quickBuyActiveVariant"
          design="plpCompact"
          class="text-right w-72"
        />
        <button
          v-if="canPurchaseProduct && (!globalContent.getIsB2c || (productHydrated.isD2C && globalContent.getNorceCheckoutEnabled)) && !userStore.isSamplesOnlyUser"
          class="btn btn--wIcon !w-32 !h-32 desk:w-40 desk:h-40"
          :aria-label="$t('quickbuy.button')"
          :disabled="productHydrated.isD2C ? !quickBuyActiveVariant.isInStock : !quickBuyActiveVariant.canAddToCart"
          :class="{
            loading: buyLoading,
          }"
          type="button"
          @click="buyProduct"
        >
          <img
            src="/icons/cart-inv.svg"
            class="inline-block w-20"
            alt="cart"
          >
        </button>
      </div>

    </td>
  </tr>

</template>

<script setup lang="ts">
import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';
import type { ProductRaw } from '#constants/types/algolia';
import type { ProductModel } from '#models/product';
import Product from '#models/product';
import { useCartStore } from '~/store/cart';
import { useGlobalContentStore } from '~/store/globalContent';
import { useUserStore } from '~/store/user';
import useLinkReplacer from '~/composables/useLinkReplacer';
import type { ProductVariantModel } from '#models/productVariant';
import VariantSelector from '~/components/product-page/VariantSelector.vue';
import ProductPrice from '~/components/product-page/ProductPrice.vue';
import StockStatus from '~/components/product/StockStatus.vue';
import PackageToAreaConverter from '~/components/product-page/PackageToAreaConverter.vue';
import usePackageAreaConverter from '~/composables/usePackageAreaConverter';
import TouchNumber from '~/components/form-elements/TouchNumber.vue';
import useProductPurchase from '~/composables/useProductPurchase';

const cartStore = useCartStore();
const userStore = useUserStore();
const globalContent = useGlobalContentStore();
const { canPurchase } = useProductPurchase();
const uiStore = useUiStore();
const { listImageSize } = storeToRefs(uiStore);

const { productUrl } = useLinkReplacer();
const props = withDefaults(defineProps<{
  product: ProductRaw | ProductModel,
  enableQuickBuy: boolean,
  placement: number, // the placement of list component
  index?: number, // the placement inside the list
  pointShop?: boolean,
  isUpsell?: boolean,
  useVariantImage?: boolean, // if the list from algolia is distinct = true, we show the variant image
  listIndex?: string,
  showStockStatus?: boolean,
}>(), {
  showStockStatus: undefined,
});

defineEmits([ 'quickbuy' ]);

const productHydrated = props.product instanceof Product ? props.product : Product.create(props.product);
if (!(props.product instanceof Product)) {
  console.warn(`Using ProductCard from ProductRaw is not recommended: ${productHydrated.partNo}`);
}

const setPrimaryToActive = () => {
  if (productHydrated) {
    let primaryVariant = props.listIndex?.includes('price')
      ? productHydrated.variants.findIndex((f) => f.partNo === props.product.partNo)
      : productHydrated.variants.findIndex((f) => f.isPrimary);
    if (primaryVariant === -1) {
      primaryVariant = 0;
    }
    quickBuyActiveVariant.value = productHydrated.variants[primaryVariant];
  }
};
const quickBuyActiveVariant = ref<ProductVariantModel>(productHydrated.variants[0]);

const {
  shouldUseArea,
} = usePackageAreaConverter(quickBuyActiveVariant);
const totalQuantity = ref(1);

setPrimaryToActive();

const link = computed<string>(() => {
  return productUrl(productHydrated.url);
});

const userPrice = computed(() => {
  return props.pointShop || props.listIndex?.includes('price') ?
    userStore.getPrice(productHydrated.partNo, props.isUpsell) :
    userStore.getPriceWithFallback(productHydrated.priceFetchPartNo, productHydrated.partNo, props.isUpsell);
});

const showLogin = () => {
  uiStore.setShowSigninModal(true, true);
};

const buyLoading = ref(false);
const buyProduct = async() => {
  buyLoading.value = true;
  await cartStore.updateCart(
    quickBuyActiveVariant.value.partNo,
    totalQuantity.value,
    -1,
    true,
    quickBuyActiveVariant.value
  );
  buyLoading.value = false;
};

// if it's floor or accessories
const isFloor = computed(() => {
  return productHydrated.categories.some((s) => {
    return s.code === 'Wood' || s.code === 'Vinyl';
  });
});

/**
 * Hides price/purchase buttons for resilient products (US/DE)
 */
const hideResilientProducts = computed(() => {
  const isResilient = productHydrated.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
  return isResilient && globalContent.disableResilientPricingAndPurchase;
});

// Hides stock status for resilient products (shows for logged in users in US)
const hideResilientStockStatus = computed(() => {
  const isResilient = productHydrated.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
  return isResilient && globalContent.disableResilientStockStatus;
});

/**
 * Check if user can purchase this product based on pricelists
 * For D2C products, always allow (B2C doesn't use pricelist restrictions)
 */
const canPurchaseProduct = computed(() => {
  if (userStore.isMediabankUser) {
    return false;
  }
  if (productHydrated.isD2C) {
    return true;
  }
  return canPurchase(productHydrated);
});

</script>

<style scoped lang="postcss">
@keyframes spin {
  from {
    background-color: #DEDDD9;
  }
  to {
    background-color: #F5F4F0;
  }
}
</style>
